import { defineMessages, type IntlShape } from 'react-intl';
import type { BlockConfigBase, BlocksFormData, JSONSchema } from '@plone/types';
import addBlocksFullWidth from '@redturtle/qubicaamf-theme/components/Blocks/extenders/addBlocksFullWidth';
import addBlocksBg from '@redturtle/qubicaamf-theme/components/Blocks/extenders/addBlocksBg';
import addBlockLines from '@redturtle/qubicaamf-theme/components/Blocks/extenders/addBlockLines';
import { addCtaFieldset, type CtaBlockExtender } from '../extenders/cta';
import type { Slide } from '@redturtle/qubicaamf-theme/components/Blocks/ImageGallery/Slides';

export interface ImageGalleryData
  extends JSONSchema,
    BlocksFormData,
    CtaBlockExtender {
  '@type': 'image_gallery';
  title: string;
  text?: object;
  slides?: Slide[];
  styles?: { bg?: string; fullwidth?: boolean };
}

const ImageGallerySchema = ({
  data,
  intl,
}: {
  data: ImageGalleryData;
  intl: IntlShape;
}): JSONSchema => {
  const schema = {
    title: 'Image Gallery',
    fieldsets: [
      {
        id: 'default',
        title: 'Default',
        fields: ['title', 'slides'],
      },
    ],
    properties: {
      title: { title: intl.formatMessage(messages.Title) },
      slides: {
        title: intl.formatMessage(messages.Slides),
        widget: 'object_list',
        schema: {
          title: 'slide',
          fieldsets: [
            {
              id: 'default',
              title: 'Default',
              fields: ['title', 'image', 'description', 'link', 'linkMore'],
            },
          ],
          properties: {
            title: {
              title: intl.formatMessage(messages.Title),
            },
            image: {
              title: intl.formatMessage(messages.slide_image),
              widget: 'object_browser',
              mode: 'image',
              return: 'single',
            },
            description: {
              title: intl.formatMessage(messages.slide_description),
            },
            link: {
              title: intl.formatMessage(messages.slide_link),
              widget: 'object_browser',
              allowExternals: true,
              mode: 'link',
              return: 'single',
            },
            linkMore: {
              title: intl.formatMessage(messages.slide_link_more_title),
              description: intl.formatMessage(
                messages.slide_link_more_description,
              ),
            },
          },
          required: ['image'],
        },
      },
    },
    required: [],
  };

  addBlocksFullWidth({ schema, formData: data, intl });
  addBlocksBg({ schema, formData: data, intl });
  addBlockLines({ schema, formData: data, intl });
  addCtaFieldset({ schema, intl });

  return schema;
};

const messages = defineMessages({
  Title: { id: 'Title', defaultMessage: 'Title' },
  Description: { id: 'Description', defaultMessage: 'Description' },
  Slides: { id: 'Slides', defaultMessage: 'Slides' },
  slide_image: { id: 'slide_image', defaultMessage: 'Image' },
  slide_description: { id: 'slide_description', defaultMessage: 'Description' },
  slide_link: { id: 'slide_link', defaultMessage: 'Link' },
  slide_link_more_title: {
    id: 'slide_link_more_title',
    defaultMessage: 'Link text',
  },
  slide_link_more_description: {
    id: 'slide_link_more_description',
    defaultMessage:
      'If provided, the link text will be shown when hovering on the image',
  },
});

export default ImageGallerySchema;

export interface ImageGalleryConfig
  extends Omit<BlockConfigBase, 'blockSchema'> {
  blockSchema: typeof ImageGallerySchema;
}
